import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react'
import {HamburgerIcon} from '@chakra-ui/icons'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'

import {
  useProductEnabled as usePaymentsOnDemandProductEnabled,
  useSideNavLinks as usePaymentsOnDemandSideNavLinks,
} from '@paymentsOnDemand/public'
import {
  useProductEnabled as usePointsOnPaymentsProductEnabled,
  useSideNavLinks as usePointsOnPaymentsSideNavLinks,
} from '@pointsOnPayments/public'
import {
  useProductEnabled as usePointsProductEnabled,
  usePointsBalance,
  useSideNavLinks as usePointsSideNavLinks,
} from '@points/public'

import {useRails} from '../../contexts/rails'
import {switchAccount} from '../../../api'
import SideNavV2 from '../../components/SideNavV2'
import {readNavCollapsed} from '../../components/SideNavV2/collapseStorage'
import {LogoHorizontal} from '../../components/Logo'
import {ToastContainer} from '../../components/Toast'
import {
  AccountBalanceWalletIcon,
  ContactSupportIcon,
  CustomerIcon,
  DashboardIcon,
  DoorOpenIcon,
  LocalAtmIcon,
  MoveToInboxIcon,
  PaymentsIcon,
  ReceiptLongIcon,
  RedeemIcon,
  SettingsIcon,
} from '../../components/SideNavV2/icons'

// SideNavV2Layout — the supplier navigation (see SupplierLayout). This is the
// "consumer" that turns real Rails/pack data into the data-driven props
// SideNavV2 renders, grouping links per product rather than flattening them.

// The packs hand us the v1 (Marmalade) icons; the v2 design uses Material
// Symbols. Rather than branch every pack on a layout flag, the v2 layer swaps
// the glyph by the link's pathname (stable; labels are i18n, hrefs may carry a
// query string). An unmapped link falls back to whatever the pack supplied.
const ICON_BY_PATH = {
  '/dashboard': <DashboardIcon />,
  '/points': <DashboardIcon />,
  '/cashin': <LocalAtmIcon />,
  '/invoices': <ReceiptLongIcon />,
  '/payments': <MoveToInboxIcon />,
  '/customers': <CustomerIcon />,
  '/points_on_payments/bills': <PaymentsIcon />,
  '/points/redemption_partners': <RedeemIcon />,
}

// Order the merged "Points on payment" links to match the design
// (Dashboard, Pay bills, Redeem); unlisted paths fall to the end in pack order.
const WALLET_PATH_ORDER = [
  '/points',
  '/points_on_payments/bills',
  '/points/redemption_partners',
]

// v2-only label overrides by pathname. The points pack labels its dashboard
// "Rewards"; v2 shows it as "Dashboard". Scoped here rather than in the shared
// locale so v1 (which flattens all products) keeps "Rewards" and avoids two
// "Dashboard" rows. Values are i18n keys resolved with t().
const LABEL_KEY_BY_PATH = {
  '/points': 'common:sideNavV2.pointsDashboardLabel',
  '/points_on_payments/bills': 'common:sideNavV2.payBillsLabel',
}

const pathOf = (href) => (href || '').split('?')[0]

// Map a pack's sidenav link onto SideNavV2's navLinkType.
// Pack shape:  {visible, disabled, label, icon, href}
// navLinkType: {label, icon, href, active, locked, comingSoon}
// (No product currently uses subLinks, so none are dropped.)
const toNavLink = (link, active) => ({
  label: link.label,
  icon: ICON_BY_PATH[pathOf(link.href)] || link.icon,
  href: link.href,
  active,
  locked: !!link.disabled,
})

// Provides the formatted M-Coin balance to its render-prop child. Mounted only
// when the Points product is enabled, so usePointsBalance's 5s poll never fires
// for non-Points suppliers (mirrors v1 gating <PointsBalance/> on the product).
const WithPointsBalance = ({children}) => {
  const {t} = useTranslation()
  const {pointsBalance} = usePointsBalance()
  return children({
    amount: pointsBalance.toLocaleString(),
    unit: t('common:marmaladeRewardCurrency'),
  })
}

WithPointsBalance.propTypes = {children: PropTypes.func.isRequired}

const SideNavV2Layout = ({children, state}) => {
  const {t} = useTranslation()
  const {organisation, authenticityToken} = useRails()

  // --- Products (grouped per product, unlike v1's flattened generalLinks) ---
  const {enabled: paymentsOnDemandEnabled} = usePaymentsOnDemandProductEnabled()
  const {enabled: pointsOnPaymentsEnabled} = usePointsOnPaymentsProductEnabled()
  const {enabled: pointsEnabled} = usePointsProductEnabled()

  const {generalLinks: paymentsOnDemandLinks} =
    usePaymentsOnDemandSideNavLinks()
  const {generalLinks: pointsOnPaymentsLinks} =
    usePointsOnPaymentsSideNavLinks()
  const {generalLinks: pointsLinks} = usePointsSideNavLinks()

  // Active nav item follows the current URL rather than a page-supplied label.
  // Points pages pass labels that don't match the pack's (and v2 overrides some
  // labels), so a URL match is what keeps them highlighted. Exact path wins;
  // otherwise the longest path prefix (for sub-pages). Falls back to the label
  // when the current page isn't itself a nav destination.
  const currentPath = window.location.pathname
  const linkPaths = [
    ...paymentsOnDemandLinks,
    ...pointsOnPaymentsLinks,
    ...pointsLinks,
  ]
    .map((link) => pathOf(link.href))
    .filter(Boolean)
  const activePath =
    linkPaths.find((p) => p === currentPath) ||
    linkPaths
      .filter((p) => currentPath.startsWith(`${p}/`))
      .sort((a, b) => b.length - a.length)[0] ||
    null

  const buildLinks = (links) =>
    links
      .filter(({visible}) => visible)
      .map((link) => {
        const active = activePath
          ? pathOf(link.href) === activePath
          : link.label === state
        const navLink = toNavLink(link, active)
        const labelKey = LABEL_KEY_BY_PATH[pathOf(link.href)]
        return labelKey ? {...navLink, label: t(labelKey)} : navLink
      })

  // The "Points on payment" product is fed by two packs: points_on_payments
  // (Pay bills) and points (Dashboard, Redeem M-Coin + the M-Coin balance).
  // We merge their links into one panel, ordered to match the v2 design.
  const orderOf = (link) => {
    const i = WALLET_PATH_ORDER.indexOf(pathOf(link.href))
    return i === -1 ? WALLET_PATH_ORDER.length : i
  }
  const walletLinks = buildLinks(
    [...pointsOnPaymentsLinks, ...pointsLinks].sort(
      (a, b) => orderOf(a) - orderOf(b),
    ),
  )

  // The accounts page has no clear active business, so the supplier-scoped
  // page links are hidden there; the rail, utilities and account row remain.
  const onAccountsPage = currentPath === '/accounts'

  const products = [
    paymentsOnDemandEnabled && {
      id: 'payments-on-demand',
      name: 'Payments on Demand',
      icon: <LocalAtmIcon />,
      links: buildLinks(paymentsOnDemandLinks),
    },
    (pointsOnPaymentsEnabled || pointsEnabled) && {
      id: 'points-on-payments',
      name: 'Marmalade Rewards',
      icon: <AccountBalanceWalletIcon />,
      links: walletLinks,
    },
  ]
    .filter(Boolean)
    .map((product) => (onAccountsPage ? {...product, links: []} : product))

  // The active product follows the current URL (the product that owns the page
  // you're on), so e.g. /points_on_payments/bills selects Points on Payment.
  // A user clicking a different product in the rail overrides this until reload.
  const [selectedProductId, setSelectedProductId] = React.useState(null)
  const urlProduct =
    products.find((product) =>
      product.links.some((link) => pathOf(link.href) === currentPath),
    ) ||
    products.find((product) =>
      product.links.some((link) =>
        currentPath.startsWith(`${pathOf(link.href)}/`),
      ),
    )
  const activeProductId = selectedProductId ?? urlProduct?.id ?? products[0]?.id

  // The nav is position: fixed, so the page content must be offset by its
  // width. SideNavV2 owns the collapse state and reports it back here so the
  // offset tracks it. Width = rail (64px) + 8px gap + panel (264px / 88px) +
  // 12px right gutter = 348px expanded, 172px collapsed.
  const [navCollapsed, setNavCollapsed] = React.useState(readNavCollapsed)
  const contentOffset = navCollapsed ? '172px' : '348px'

  // Below `lg` the nav is hidden behind a top bar and opens in a drawer.
  const drawer = useDisclosure()

  // --- Account row: the menu lists the top businesses (current first, so it
  // is always visible), with a "View all" link to the accounts page for the
  // full list. Selecting a business switches to it and lands on its dashboard.
  // On the accounts page itself "View all" is the selected item (shown on the
  // trigger) and no business is highlighted, ready for a new selection. ---
  const currentSupplier = organisation.currentSupplier || {}
  const initialOf = (name) => (name || '?').charAt(0).toUpperCase()

  const selectBusiness = (accountId) => {
    switchAccount({accountId, authenticityToken})
      .then(() => window.location.assign('/dashboard'))
      .catch(() => toast.error(t('common:sideNavV2.switchBusinessError')))
  }

  const switchableSuppliers = (organisation.availableSuppliers || []).filter(
    (supplier) => supplier.accountId,
  )
  const topSuppliers = [
    ...switchableSuppliers.filter((s) => s.id === currentSupplier.id),
    ...switchableSuppliers.filter((s) => s.id !== currentSupplier.id),
  ].slice(0, 2)

  const viewAllLabel = t('common:sideNavV2.viewAllLabel')
  const account = {
    initial: initialOf(onAccountsPage ? viewAllLabel : currentSupplier.name),
    name: onAccountsPage ? viewAllLabel : currentSupplier.name || '',
    businesses: topSuppliers.map((supplier) => ({
      key: supplier.accountId,
      initial: initialOf(supplier.name),
      name: supplier.name || '',
      current: !onAccountsPage && supplier.id === currentSupplier.id,
      onSelect: () => selectBusiness(supplier.accountId),
    })),
    viewAll: {
      label: viewAllLabel,
      href: '/accounts',
      current: onAccountsPage,
    },
  }

  // Rewards and Notifications are intentionally omitted for now. Help links out
  // to the external support desk; Settings goes to the in-app settings screen.
  const utilityActions = [
    {
      key: 'support',
      label: 'Help & support',
      icon: <ContactSupportIcon />,
      href: 'https://marmalade.ladesk.com/',
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: <SettingsIcon />,
      href: '/settings',
    },
  ]

  // The M-Coin balance belongs to the Points (rewards) pack specifically — a
  // supplier with Points on Payments but not Points has no balance to show.
  const navProps = {
    products,
    activeProductId,
    onProductChange: setSelectedProductId,
    utilityActions,
    signOutAction: '/sign_out',
    signOutIcon: <DoorOpenIcon />,
    authenticityToken,
    account,
  }

  const renderNav = (balance) => (
    <>
      {/* Desktop (lg+): fixed rail + panel, content offset by its width. */}
      <Box display={{base: 'none', lg: 'block'}}>
        <SideNavV2
          {...navProps}
          balance={balance}
          onCollapse={setNavCollapsed}
        />
      </Box>

      {/* Mobile (<lg): top bar with a hamburger that opens the nav in a drawer. */}
      <Flex
        display={{base: 'flex', lg: 'none'}}
        position="fixed"
        top="0"
        left="0"
        right="0"
        zIndex="9"
        align="center"
        gap="8px"
        h="56px"
        px="12px"
        bg="mld.neutral.0"
        borderBottom="1px solid"
        borderColor="mld.neutral.200"
      >
        <IconButton
          aria-label="Open navigation"
          variant="ghost"
          color="mld.neutral.900"
          icon={<HamburgerIcon boxSize="24px" />}
          onClick={drawer.onOpen}
        />
        <LogoHorizontal width={140} height={16} />
      </Flex>

      <Drawer
        isOpen={drawer.isOpen}
        placement="left"
        size="full"
        onClose={drawer.onClose}
      >
        <DrawerOverlay />
        <DrawerContent>
          <SideNavV2
            {...navProps}
            balance={balance}
            embedded
            onClose={drawer.onClose}
            onCollapse={() => {}}
          />
        </DrawerContent>
      </Drawer>

      <Box
        ml={{base: 0, lg: contentOffset}}
        pt={{base: '56px', lg: 0}}
        transition="margin-left 0.2s ease"
      >
        <ToastContainer />
        {children}
      </Box>
    </>
  )

  return pointsEnabled ? (
    <WithPointsBalance>{renderNav}</WithPointsBalance>
  ) : (
    renderNav(undefined)
  )
}

SideNavV2Layout.propTypes = {
  children: PropTypes.node.isRequired,
  // Active link label, used to mark the current nav item (as in SupplierLayout).
  state: PropTypes.string.isRequired,
}

export default SideNavV2Layout
